import React from 'react'
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {QuestionType, RowData} from '../types'

interface Colors {
    primariaEscura: string
    botaoFormulario: string
}

interface TableQuestionProps {
    question: QuestionType
    newTables: RowData[][]
    onAddTable: () => void
    onAddRow: (tableIndex: number) => void
    onDeleteTable: (tableIndex: number) => void
    onDeleteRow: (tableIndex: number, rowId: number) => void
    onInputChangeTable: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        tableIndex: number,
        rowIndex: number,
        field: keyof RowData
    ) => void
    calculateTableTotal: (table: RowData[]) => number
    calculateGlobalTotal: () => number
    formatCurrency: (value: number) => string
    colors: Colors
}

export default function TableQuestion({
    question,
    newTables,
    onAddTable,
    onAddRow,
    onDeleteTable,
    onDeleteRow,
    onInputChangeTable,
    calculateTableTotal,
    calculateGlobalTotal,
    formatCurrency,
    colors,
}: TableQuestionProps) {
    const tableType = question.type.table?.type
    const isBooleanTable = tableType === 'boolean_description'

    const renderTable = (table: RowData[], tableIndex: number) => (
        <Box
            component={Paper}
            sx={{
                width: '100%',
                backgroundColor: '#F6F8FA',
                marginBottom: 4,
                position: 'relative',
                boxShadow: 'none',
                borderRadius: '4px',
                overflow: 'auto',
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    p: 2,
                    backgroundColor: colors.primariaEscura,
                    color: 'white',
                    margin: 0,
                    borderRadius: '4px 4px 0 0',
                    fontWeight: 'bold',
                }}
            >
                {window.location.hostname.toLowerCase().includes('finep')
                    ? `Grupo ${tableIndex + 1}`
                    : `Lote ${tableIndex + 1}`}
            </Typography>
            <Stack spacing={2} sx={{p: 2}}>
                {table.map((row, rowIndex) => (
                    <Paper
                        key={rowIndex}
                        elevation={1}
                        sx={{
                            p: 3,
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                            position: 'relative',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    backgroundColor: colors.primariaEscura,
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem',
                                }}
                            >
                                Item {rowIndex + 1}
                            </Typography>

                            {question.type.table?.type !==
                                'boolean_description' &&
                                table.length > 1 && (
                                    <IconButton
                                        onClick={() =>
                                            onDeleteRow(tableIndex, row.id)
                                        }
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                        </Box>
                        <Stack spacing={2}>
                            {question.type.table?.headers.map(
                                (header, headerIndex) => {
                                    let field: keyof RowData
                                    let isReadOnly = false
                                    let isNumeric = false
                                    let hasCurrency = false

                                    if (isBooleanTable) {
                                        if (
                                            header.label
                                                .toLowerCase()
                                                .includes('documento')
                                        ) {
                                            field = 'documento'
                                            isReadOnly = true
                                        } else if (
                                            header.label
                                                .toLowerCase()
                                                .includes('possui') ||
                                            header.name
                                                .toLowerCase()
                                                .includes('bool')
                                        ) {
                                            field = 'bool'
                                        } else if (
                                            header.label
                                                .toLowerCase()
                                                .includes('descrição') ||
                                            header.label
                                                .toLowerCase()
                                                .includes('descricao')
                                        ) {
                                            field = 'descricao'
                                        } else {
                                            field =
                                                header.label as keyof RowData
                                        }
                                    } else {
                                        const lowerLabel =
                                            header.label?.toLowerCase()
                                        if (
                                            lowerLabel.includes('quantity') ||
                                            lowerLabel.includes('quantidade')
                                        ) {
                                            field = 'quantity'
                                            isNumeric = true
                                        } else if (
                                            lowerLabel.includes(
                                                'total_price'
                                            ) ||
                                            lowerLabel.includes('total')
                                        ) {
                                            field = 'total_price'
                                            isReadOnly = true
                                            hasCurrency = true
                                        } else if (
                                            lowerLabel.includes('price') ||
                                            lowerLabel.includes('preço') ||
                                            lowerLabel.includes('preco')
                                        ) {
                                            field = 'price'
                                            hasCurrency = true
                                        } else if (
                                            lowerLabel.includes('name') ||
                                            lowerLabel.includes('nome')
                                        ) {
                                            field = 'name'
                                        } else {
                                            field =
                                                header.label as keyof RowData
                                        }
                                    }

                                    if (row[field] === undefined) {
                                        ;(row as any)[field] =
                                            isBooleanTable && field === 'bool'
                                                ? 'Não'
                                                : ''
                                    }

                                    const rawValue = row[field]
                                    const displayValue =
                                        isReadOnly && hasCurrency
                                            ? formatCurrency(
                                                  parseFloat(
                                                      String(rawValue).replace(
                                                          ',',
                                                          '.'
                                                      )
                                                  ) || 0
                                              )
                                            : rawValue
                                    const showCurrencyAdornment =
                                        hasCurrency && !isReadOnly

                                    return (
                                        <Box key={headerIndex}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    mb: 1,
                                                    color: '#012441',
                                                }}
                                            >
                                                {header.name}:
                                            </Typography>
                                            <TextField
                                                select={field === 'bool'}
                                                multiline={
                                                    field === 'descricao' ||
                                                    field === 'name'
                                                }
                                                rows={
                                                    field === 'descricao'
                                                        ? 3
                                                        : 1
                                                }
                                                value={displayValue}
                                                onChange={(e) =>
                                                    !isReadOnly &&
                                                    onInputChangeTable(
                                                        e,
                                                        tableIndex,
                                                        rowIndex,
                                                        field
                                                    )
                                                }
                                                variant="outlined"
                                                size="small"
                                                fullWidth
                                                disabled={
                                                    isReadOnly ||
                                                    header.name.toLowerCase() ===
                                                        'documento'
                                                }
                                                type={
                                                    isNumeric
                                                        ? 'number'
                                                        : 'text'
                                                }
                                                inputProps={{
                                                    inputMode: isNumeric
                                                        ? 'numeric'
                                                        : 'text',
                                                    pattern: isNumeric
                                                        ? '[0-9]*'
                                                        : undefined,
                                                }}
                                                InputProps={{
                                                    startAdornment:
                                                        showCurrencyAdornment ? (
                                                            <InputAdornment position="start">
                                                                R$
                                                            </InputAdornment>
                                                        ) : null,
                                                    readOnly: isReadOnly,
                                                }}
                                            >
                                                {field === 'bool' && [
                                                    <MenuItem
                                                        key="sim"
                                                        value={'Sim'}
                                                    >
                                                        Sim
                                                    </MenuItem>,
                                                    <MenuItem
                                                        key="nao"
                                                        value={'Não'}
                                                    >
                                                        Não
                                                    </MenuItem>,
                                                ]}
                                            </TextField>
                                        </Box>
                                    )
                                }
                            )}
                        </Stack>
                    </Paper>
                ))}
            </Stack>
            {tableType === 'default' && (
                <Box
                    sx={{
                        marginTop: 2,
                        padding: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => onAddRow(tableIndex)}
                        sx={{
                            color: '#012441',
                            backgroundColor: 'transparent',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                border: 'none',
                            },
                        }}
                    >
                        Adicionar item
                    </Button>
                    {tableIndex !== 0 && (
                        <Button
                            startIcon={<DeleteIcon />}
                            onClick={() => onDeleteTable(tableIndex)}
                            sx={{
                                color: '#d32f2f',
                                backgroundColor: 'transparent',
                                border: 'none',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                },
                            }}
                        >
                            Apagar todo o{' '}
                            {window.location.hostname
                                .toLowerCase()
                                .includes('finep')
                                ? 'Grupo'
                                : 'Lote'}
                        </Button>
                    )}
                </Box>
            )}
            {tableType === 'boolean_description' && (
                <Box
                    sx={{
                        marginTop: 2,
                        padding: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 6,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 'medium',
                            color: '#012441',
                        }}
                    >
                        Preço global do{' '}
                        {window.location.hostname
                            .toLowerCase()
                            .includes('finep')
                            ? 'Grupo'
                            : 'Lote'}
                        : {formatCurrency(calculateTableTotal(table))}
                    </Typography>
                </Box>
            )}
        </Box>
    )

    return (
        <>
            {newTables.map((table, tableIndex) =>
                renderTable(table, tableIndex)
            )}
            {tableType === 'default' && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        marginTop: 2,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: '#012441',
                        }}
                    >
                        Preço global da contratação:{' '}
                        {formatCurrency(calculateGlobalTotal())}
                    </Typography>
                    <Button
                        onClick={onAddTable}
                        startIcon={<AddIcon />}
                        sx={{
                            color: '#012441',
                            backgroundColor: 'transparent',
                            border: 'none',
                            '&:hover': {
                                backgroundColor: 'transparent',
                                border: 'none',
                            },
                        }}
                    >
                        Adicionar{' '}
                        {window.location.hostname
                            .toLowerCase()
                            .includes('finep')
                            ? 'Grupo'
                            : 'Lote'}
                    </Button>
                </Box>
            )}
        </>
    )
}
